// "Set this field for many rows at once" control above the review grid.
//
// The version this replaces (ChatAssistant handleBulkUpdate) hardcoded a field-name
// if/else chain and always overwrote EVERY row. Two changes here: the field list comes
// from the model descriptor, so a new model gets bulk edit for free; and the user picks
// a scope, because "fill in the blanks" is the case that actually comes up - a file
// missing Unit or Currency on some rows shouldn't force you to clobber the rows that
// did have them.
import {
    Autocomplete,
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { months } from '../../../../Helpers';
import { ImportContext, getEntityLabel } from './ImportRow';
import type { ImportFieldDescriptor } from './ImportModelRegistry';

export type BulkEditScope = 'all' | 'empty' | 'selected';

export interface BulkEditToolbarProps {
    fields: ImportFieldDescriptor[];
    ctx: ImportContext;
    /** Number of rows currently selected in the grid, for the 'selected' scope. */
    selectedCount: number;
    onApply: (fieldKey: string, value: unknown, scope: BulkEditScope) => void;
}

export default function BulkEditToolbar(props: BulkEditToolbarProps) {
    const { fields, ctx, selectedCount, onApply } = props;

    const [fieldKey, setFieldKey] = useState<string>('');
    const [value, setValue] = useState<unknown>('');
    const [scope, setScope] = useState<BulkEditScope>('empty');

    const field = useMemo(() => fields.find(f => f.key === fieldKey), [fields, fieldKey]);

    // Options for whichever kind of field is selected.
    const options = useMemo<string[]>(() => {
        if (!field) return [];
        if (field.kind === 'enum' && field.enumValues) return Object.values(field.enumValues);
        if (field.kind === 'month') return [...months];
        if (field.kind === 'entity' && field.entitySource) {
            const source = field.entitySource;
            const collection = source === 'budgetPricingStrategies'
                ? (ctx.pricingStrategies ?? [])
                : source === 'budgetRounds'
                    ? (ctx.customerData?.budgetRounds ?? [])
                    : source === 'commodities'
                        ? (ctx.customerData?.commodities ?? [])
                        : source === 'counterparties'
                            ? (ctx.customerData?.counterparties ?? [])
                            : source === 'locations'
                                ? (ctx.customerData?.locations ?? [])
                                : (ctx.customerData?.portfolios ?? []);
            return collection
                .map(entity => getEntityLabel(source, entity))
                .filter(label => label.length > 0);
        }
        return [];
    }, [field, ctx]);

    const usesOptions = options.length > 0;
    const canApply = !!field
        && value !== ''
        && value !== undefined
        && value !== null
        && (scope !== 'selected' || selectedCount > 0);

    function handleApply() {
        if (!field) return;
        onApply(field.key, value, scope);
    }

    return (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    Set for many rows
                </Typography>

                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="bulk-field-label">Field</InputLabel>
                    <Select
                        labelId="bulk-field-label"
                        label="Field"
                        value={fieldKey}
                        onChange={event => {
                            setFieldKey(event.target.value);
                            // A value carried over from a different field is never valid.
                            setValue('');
                        }}
                    >
                        {fields.map(f => (
                            <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {usesOptions ? (
                    <Autocomplete
                        size="small"
                        sx={{ minWidth: 220 }}
                        options={options}
                        value={(value as string) || null}
                        onChange={(_event, next) => setValue(next ?? '')}
                        renderInput={params => <TextField {...params} label="Value" />}
                        disabled={!field}
                    />
                ) : (
                    <TextField
                        size="small"
                        sx={{ minWidth: 220 }}
                        label="Value"
                        type={field?.kind === 'number' || field?.kind === 'year' ? 'number' : 'text'}
                        value={(value as string) ?? ''}
                        onChange={event => setValue(event.target.value)}
                        disabled={!field}
                        placeholder={field?.kind === 'date' ? 'YYYY-MM-DD' : undefined}
                    />
                )}

                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="bulk-scope-label">Apply to</InputLabel>
                    <Select
                        labelId="bulk-scope-label"
                        label="Apply to"
                        value={scope}
                        onChange={event => setScope(event.target.value as BulkEditScope)}
                    >
                        <MenuItem value="empty">Only rows where it is blank</MenuItem>
                        <MenuItem value="all">All rows</MenuItem>
                        <MenuItem value="selected" disabled={selectedCount === 0}>
                            {selectedCount > 0 ? `Selected rows (${selectedCount})` : 'Selected rows'}
                        </MenuItem>
                    </Select>
                </FormControl>

                <Button size="small" disabled={!canApply} onClick={handleApply}>
                    Apply
                </Button>
            </Box>
        </Paper>
    );
}
